import React, { useEffect, useState } from "react";
import { Button, Empty, Grid, Input, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";
import FileUtils from "../../../core/utils/file-utils";
import Helper from "../../../core/Helper";
import { useModal } from "../../../core/context/ModalContext";
import { PERMISSIONS, useUser } from "../../../core/context/UserContext";
import BillingStatusTag, { BILLING_STATUS } from "./BillingStatusTag";
import InvoiceForm from "./InvoiceForm";
import CoPaymentForm from "./CoPaymentForm";

const { Text } = Typography;

const PAVO_SOURCE = 1;

export default function AppointmentInvoicePanel({ row, onChange }) {
    const { hasPermission, formatPrice } = useUser();
    const { openModal, closeModal } = useModal();
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [appointment, setAppointment] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;
    const canManage = hasPermission(PERMISSIONS.INVOICE_MANAGE);
    const canCancel = hasPermission(PERMISSIONS.INVOICE_CANCEL);
    const appointmentId = row.appointmentId;

    const load = async () => {
        const [invoiceRes, paymentRes, appointmentRes] = await Promise.all([
            ApiService.Invoice.GetAppointmentInvoices(appointmentId),
            ApiService.Payment.GetPaymentList(appointmentId),
            ApiService.Appointment.GetAppointment(appointmentId),
        ]);
        if (invoiceRes.success) setInvoices(invoiceRes.data);
        if (paymentRes.success) setPayments(paymentRes.data);
        if (appointmentRes.success) setAppointment(appointmentRes.data);
    };

    useEffect(() => { load(); }, [appointmentId]);

    const refresh = async () => {
        await load();
        onChange && onChange();
    };

    const openInvoiceForm = () => {
        openModal(t('Add Invoice'), (
            <InvoiceForm
                appointments={[{ ...row, totalPrice: appointment?.totalPrice ?? row.totalPrice, currency: appointment?.currency ?? row.currency }]}
                onSaved={async () => { closeModal(); await refresh(); }}
            />
        ), () => { }, { width: 560 });
    };

    const openCoPaymentForm = () => {
        openModal(t('Split Co-Payment'), (
            <CoPaymentForm appointmentId={appointmentId} onSaved={async () => { closeModal(); await refresh(); }} />
        ), () => { }, { width: 480 });
    };

    const setMark = async (value) => {
        const res = await ApiService.Invoice.SetBillingMark([appointmentId], value);
        if (res.success) await refresh();
    };

    const download = async (invoice) => {
        const res = await ApiService.Invoice.DownloadFile(invoice.id);
        if (res.success) window.open(res.data, '_blank');
    };

    const addFile = async (invoice) => {
        const file = await FileUtils.getFileAsBase64({ accept: '.pdf,.jpg,.jpeg,.png' });
        const res = await ApiService.Invoice.AddFile(invoice.id, { originalFileName: file.fileName, base64Data: file.data, fileType: 3 });
        if (res.success) await load();
    };

    const cancelInvoice = async (invoice) => {
        const res = await ApiService.Invoice.CancelInvoice(invoice.id, cancelReason);
        setCancelReason('');
        if (res.success) await refresh();
    };

    const linkPayment = async (invoice, paymentId) => {
        const res = await ApiService.Invoice.LinkPayment(invoice.id, paymentId);
        if (res.success) await load();
    };

    const unlinkPayment = async (paymentId) => {
        const res = await ApiService.Invoice.UnlinkPayment(paymentId);
        if (res.success) await load();
    };

    const unlinkedPayments = payments.filter(x => !x.isDeleted && !x.invoiceId);

    const renderPayments = (invoice) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Text strong style={{ fontSize: 12 }}>{t('Linked Payments')}</Text>
            {invoice.payments.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>-</Text>}
            {invoice.payments.map(payment => (
                <Space key={payment.id} size={8}>
                    <Text style={{ fontSize: 12 }}>{Helper.Date.dateFormat(payment.paymentDate)}</Text>
                    <Text style={{ fontSize: 12 }}>{formatPrice(payment.amount, { currency: invoice.currency })}</Text>
                    {payment.paymentMethodName && <Text type="secondary" style={{ fontSize: 12 }}>{payment.paymentMethodName}</Text>}
                    {canManage && invoice.source !== PAVO_SOURCE && !invoice.isDeleted && (
                        <Button size="small" type="link" onClick={() => unlinkPayment(payment.id)}>{t('Unlink')}</Button>
                    )}
                </Space>
            ))}
            {canManage && invoice.source !== PAVO_SOURCE && !invoice.isDeleted && unlinkedPayments.length > 0 && (
                <Select
                    size="small"
                    style={{ maxWidth: 320 }}
                    placeholder={t('Link Payment')}
                    value={null}
                    onChange={(paymentId) => linkPayment(invoice, paymentId)}
                    options={unlinkedPayments.map(x => ({
                        value: x.id,
                        label: `${Helper.Date.dateFormat(x.paymentDate)} - ${formatPrice(x.currencyAmount, { currency: invoice.currency })}`,
                    }))}
                />
            )}
        </div>
    );

    const renderActions = (invoice) => (
        <Space size={2} wrap>
            {invoice.fileName && (
                <Button size="small" type="link" onClick={() => download(invoice)}>{t('Download')}</Button>
            )}
            {invoice.pavoInquiryLink && (
                <Button size="small" type="link" href={invoice.pavoInquiryLink} target="_blank" rel="noreferrer">{t('View on Pavo')}</Button>
            )}
            {canManage && !invoice.isDeleted && invoice.source !== PAVO_SOURCE && (
                <Button size="small" type="link" onClick={() => addFile(invoice)}>{t('Attach File')}</Button>
            )}
            {canCancel && !invoice.isDeleted && invoice.source !== PAVO_SOURCE && (
                <Popconfirm
                    title={t('Cancel Invoice')}
                    description={(
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 260 }}>
                            {invoice.appointmentIds.length > 1 && (
                                <Text type="warning" style={{ fontSize: 12 }}>
                                    {t('This invoice is linked to these appointments, their billing status will change')}: {invoice.appointmentIds.map(x => `#${x}`).join(', ')}
                                </Text>
                            )}
                            <Input size="small" placeholder={t('Reason')} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                        </div>
                    )}
                    okText={t('Confirm')}
                    cancelText={t('Back')}
                    onConfirm={() => cancelInvoice(invoice)}
                    okButtonProps={{ disabled: !cancelReason.trim() }}
                >
                    <Button size="small" type="link" danger>{t('Cancel')}</Button>
                </Popconfirm>
            )}
        </Space>
    );

    const renderSource = (invoice) => (
        <Space size={4} wrap>
            <Tag style={{ margin: 0 }}>{invoice.source === PAVO_SOURCE ? 'PAVO' : t('Manual')}</Tag>
            {invoice.isOss && <Tag color="purple" style={{ margin: 0 }}>{t('OSS')}</Tag>}
        </Space>
    );

    const renderInvoiceNo = (invoice) => (
        <div>
            <div>{invoice.invoiceNo || '-'}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>{invoice.operationName || invoice.documentType || ''}</Text>
            {invoice.isDeleted && <div><Text type="danger" style={{ fontSize: 11 }}>{t('Cancelled')}: {invoice.cancelReason}</Text></div>}
        </div>
    );

    const renderAmount = (invoice) => (
        <div>
            <div>{formatPrice(invoice.appointmentAmount, { currency: invoice.currency })}</div>
            {invoice.appointmentIds.length > 1 && (
                <Text type="secondary" style={{ fontSize: 11 }}>{t('Total')}: {formatPrice(invoice.totalAmount, { currency: invoice.currency })}</Text>
            )}
        </div>
    );

    const columns = isMobile
        ? [{
            key: 'invoice', title: t('Invoices'), render: (_, invoice) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        {renderSource(invoice)}
                        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{Helper.Date.dateFormat(invoice.invoiceDate)}</Text>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        {renderInvoiceNo(invoice)}
                        <div style={{ textAlign: 'right' }}>{renderAmount(invoice)}</div>
                    </div>
                    {renderActions(invoice)}
                </div>
            )
        }]
        : [
            { key: 'source', title: t('Source'), width: 110, render: (_, invoice) => renderSource(invoice) },
            { key: 'invoiceNo', title: t('Invoice No'), render: (_, invoice) => renderInvoiceNo(invoice) },
            { key: 'invoiceDate', dataIndex: 'invoiceDate', title: t('Date'), width: 120, render: (value) => Helper.Date.dateFormat(value) },
            { key: 'amount', title: t('Amount'), align: 'right', width: 130, render: (_, invoice) => renderAmount(invoice) },
            { key: 'actions', title: '', width: 220, render: (_, invoice) => renderActions(invoice) },
        ];

    const isManualMarked = !!appointment?.billingStatusManual;
    const activeInvoiceCount = invoices.filter(x => !x.isDeleted).length;
    const isBilled = appointment?.billingStatus === BILLING_STATUS.BILLED;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Space size={8}>
                    <Text strong>#{appointmentId}</Text>
                    <BillingStatusTag status={appointment?.billingStatus ?? row.billingStatus} />
                </Space>
                {canManage && (
                    <Space size={8} wrap>
                        {appointment?.billingStatus === BILLING_STATUS.NONE && (
                            <Button size="small" onClick={() => setMark(true)}>{t('Mark To Be Billed')}</Button>
                        )}
                        {isManualMarked && activeInvoiceCount === 0 && (
                            <Button size="small" onClick={() => setMark(false)}>{t('Remove To Be Billed Mark')}</Button>
                        )}
                        {!isBilled && <Button size="small" onClick={openCoPaymentForm}>{t('Split Co-Payment')}</Button>}
                        {!isBilled && <Button size="small" type="primary" onClick={openInvoiceForm}>{t('Add Invoice')}</Button>}
                    </Space>
                )}
            </div>

            <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={invoices}
                scroll={isMobile ? undefined : { x: 760 }}
                locale={{ emptyText: <Empty description={t('No Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
                onRow={(invoice) => ({ style: { opacity: invoice.isDeleted ? 0.6 : 1 } })}
                expandable={{ expandedRowRender: renderPayments }}
                columns={columns}
            />
        </div>
    );
}
